/*
  risk-free-card.js — as duas partes vivas do cartão de garantia
  (sections/risk-free-card.liquid).

  1. CONTADOR ATÉ O HORÁRIO DE CORTE

     A referência que copiamos subtrai cada unidade isoladamente —
     `24 - hora`, `60 - minuto`, `60 - segundo` — e nunca faz o
     empréstimo. O resultado: os segundos mostram "60" a cada virada de
     minuto, o relógio nunca chega a zero e o total fica sempre 1h01m01s
     maior que o prazo real (medido: às 15:15:31 ela exibia 9:45:29
     quando faltavam 8:44:28 para a meia-noite).

     Aqui a conta é a diferença em milissegundos até o corte, o que
     resolve empréstimo e zero à esquerda de graça.

  2. COERÊNCIA ENTRE CONTADOR E DATA

     Passado o corte, o alvo vira o corte do dia seguinte. Se ficasse só
     nisso, o cartão diria "peça em 23:50 e receba dia X" com o X ainda
     calculado como se o pedido saísse hoje. Por isso, depois do corte,
     os dias extras configurados entram nos data-min/data-max da linha do
     tempo e ela é remontada.

     A remontagem cobre as duas ordens possíveis de carga: se o
     delivery-timeline.js já definiu o elemento, `render()` existe e é
     chamado; se ainda não, basta ter mudado os data-* — ele lê os
     valores já corrigidos quando for a vez dele.

  3. BOTÃO

     Como na referência, o CTA não adiciona ao carrinho: rola até o
     formulário de compra. A lista de alvos e o desconto do cabeçalho
     fixo são os mesmos do sticky-add-to-cart.js.
*/
if (!customElements.get('risk-free-card')) {
  customElements.define(
    'risk-free-card',
    class RiskFreeCard extends HTMLElement {
      connectedCallback() {
        this.cta = this.querySelector('[data-rfc-cta]');
        this.counters = this.querySelectorAll('[data-rfc-countdown]');

        if (this.cta) {
          this.onClick = this.onClick.bind(this);
          this.cta.addEventListener('click', this.onClick);
        }

        this.applyCutoffShift();

        if (this.counters.length) {
          this.target = this.nextCutoff();
          this.tick();
          this.timer = setInterval(() => this.tick(), 1000);
        }
      }

      disconnectedCallback() {
        if (this.timer) clearInterval(this.timer);
        if (this.cta) this.cta.removeEventListener('click', this.onClick);
      }

      /* ---------- Corte ---------- */

      /* Aceita "16:00" e "16"; qualquer coisa fora disso vira meia-noite,
         que é o comportamento mais previsível para quem digitou errado. */
      cutoffParts() {
        const raw = (this.dataset.cutoff || '').split(':');
        const hour = Math.min(23, Math.max(0, parseInt(raw[0], 10) || 0));
        const minute = Math.min(59, Math.max(0, parseInt(raw[1], 10) || 0));
        return [hour, minute];
      }

      todayCutoff() {
        const [hour, minute] = this.cutoffParts();
        const date = new Date();
        date.setHours(hour, minute, 0, 0);
        return date;
      }

      nextCutoff() {
        const date = this.todayCutoff();
        if (date.getTime() <= Date.now()) date.setDate(date.getDate() + 1);
        return date;
      }

      pastCutoff() {
        return Date.now() >= this.todayCutoff().getTime();
      }

      applyCutoffShift() {
        const extra = parseInt(this.dataset.extraDays, 10) || 0;
        if (extra <= 0 || !this.pastCutoff()) return;

        const timeline = this.querySelector('delivery-timeline');
        if (!timeline) return;

        timeline.querySelectorAll('[data-timeline-date]').forEach((element) => {
          element.dataset.min = String((parseInt(element.dataset.min, 10) || 0) + extra);
          element.dataset.max = String((parseInt(element.dataset.max, 10) || 0) + extra);
        });

        if (typeof timeline.render === 'function') timeline.render();
      }

      /* ---------- Contador ---------- */

      tick() {
        let remaining = this.target.getTime() - Date.now();

        if (remaining <= 0) {
          this.target = this.nextCutoff();
          /* A virada do corte é o momento em que a data de entrega
             escorrega — recalcula antes de pintar o novo tempo. */
          this.applyCutoffShift();
          remaining = this.target.getTime() - Date.now();
        }

        const total = Math.floor(remaining / 1000);
        const hours = Math.floor(total / 3600);
        const minutes = Math.floor((total % 3600) / 60);
        const seconds = total % 60;
        const text = `${hours}:${this.pad(minutes)}:${this.pad(seconds)}`;

        this.counters.forEach((element) => {
          if (element.textContent !== text) element.textContent = text;
        });
      }

      pad(number) {
        return String(number).padStart(2, '0');
      }

      /* ---------- Botão ---------- */

      onClick(event) {
        const target = this.resolveTarget();

        if (!target) {
          /* Sem formulário na página (uma home, por exemplo) o botão não
             pode ficar morto: cai no link de reserva, se houver. */
          const fallback = this.dataset.fallbackHref;
          if (fallback) window.location.href = fallback;
          return;
        }

        event.preventDefault();

        const top = Math.max(
          0,
          window.scrollY + target.getBoundingClientRect().top - this.headerOffset() - 16
        );
        window.scrollTo({ top, behavior: this.prefersReducedMotion() ? 'auto' : 'smooth' });

        /* Leva o foco junto: sem isso quem navega por teclado continua no
           fim da página depois que a tela já voltou para o seletor. */
        if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
      }

      resolveTarget() {
        const custom = this.dataset.scrollTarget;
        if (custom) {
          try {
            const found = document.querySelector(custom);
            if (found) return found;
          } catch (error) {
            /* Seletor inválido digitado no editor: segue para a lista padrão. */
          }
        }

        const candidates = [
          '.product__bundle',
          'bundle-variantes',
          'variant-selects',
          '.product-form__input',
          '.product-form__buttons',
          '.shopify-product-form',
        ];

        for (const selector of candidates) {
          const found = document.querySelector(selector);
          if (found) return found;
        }

        return null;
      }

      headerOffset() {
        if (!document.querySelector('sticky-header')) return 0;
        const declared = parseFloat(
          getComputedStyle(document.documentElement).getPropertyValue('--header-height')
        );
        if (Number.isFinite(declared) && declared > 0) return declared;
        const header = document.querySelector('.section-header');
        return header ? header.offsetHeight : 0;
      }

      prefersReducedMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }
    }
  );
}
